import { ReactElement, useMemo } from 'react';
import { RiDeleteBinLine } from 'react-icons/ri';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { BPMTable, Header } from '@Components/StickyTable';
import { formatLastEdited, SavedPlan } from './streaming-media-planner-utils';

interface SavedPlansTabProps {
  plans: SavedPlan[];
  onOpen: (plan: SavedPlan) => void;
  onDelete: (plan: SavedPlan) => void;
}

const renderEmpty = (): ReactElement => (
  <div className='saved-list-empty'>
    No saved plans yet. Use <strong>Save Plan</strong> on the My Plan tab to save one.
  </div>
);

export const SavedPlansTab = ({ plans, onOpen, onDelete }: SavedPlansTabProps): ReactElement => {
  const headers = useMemo<Header[]>(
    () => [
      {
        name: 'id',
        label: '#',
        width: 64,
        renderer: (plan: SavedPlan) => (
          <InteractiveDiv className='saved-list-cell' onClick={() => onOpen(plan)}>
            {plan.id}
          </InteractiveDiv>
        )
      },
      {
        name: 'name',
        label: 'Name',
        flex: 1,
        renderer: (plan: SavedPlan) => (
          <InteractiveDiv className='saved-list-cell saved-list-name' onClick={() => onOpen(plan)}>
            {plan.name}
          </InteractiveDiv>
        )
      },
      {
        name: 'lastEditor',
        label: 'Last Editor',
        width: 220,
        renderer: (plan: SavedPlan) => (
          <InteractiveDiv className='saved-list-cell' onClick={() => onOpen(plan)}>
            {plan.lastEditor || '—'}
          </InteractiveDiv>
        )
      },
      {
        name: 'lastEditedAt',
        label: 'Last Edited',
        width: 180,
        contentGetter: (plan: SavedPlan) => plan.lastEditedAt,
        renderer: (plan: SavedPlan) => (
          <InteractiveDiv className='saved-list-cell' onClick={() => onOpen(plan)}>
            {formatLastEdited(plan.lastEditedAt)}
          </InteractiveDiv>
        )
      },
      {
        name: 'actions',
        label: '',
        width: 56,
        nonInteractive: true,
        nonFilterable: true,
        renderer: (plan: SavedPlan) => (
          <Button
            appearance={Appearance.EMPTY}
            size={Size.SM}
            onClick={() => onDelete(plan)}
            tooltip={{ text: 'Delete plan' }}
          >
            <RiDeleteBinLine />
          </Button>
        )
      }
    ],
    [onOpen, onDelete]
  );

  return (
    <div className='saved-list-container'>
      <BPMTable
        data={plans}
        headers={headers}
        filterBar={false}
        rowHeight={44}
        noRowsRenderer={renderEmpty}
      />
    </div>
  );
};
